import { AlertCircle, Lightbulb } from "lucide-react";
import { useTranslation } from "react-i18next";

const EmotionalSection = ({ report }) => {
  const { t } = useTranslation();
  // Defensive checks
  const emotionalData = report?.emotional_ai || {};

  const summaryInsight = emotionalData.summary_insight ||  t("emotional.noInsightsAvailable");
  const overallEmotionalState = emotionalData.overall_emotional_state || "N/A";
  const frustrationLevel = emotionalData.frustration_level || "N/A";
  const confidenceTrend = emotionalData.confidence_trend || "N/A";
  const motivationLevel = emotionalData.motivation_level || "N/A";
  const stressIndicators = emotionalData.stress_indicators || [];
  const supportRecommendations = emotionalData.support_recommendations || [];
  const interventionNeeded = emotionalData.intervention_needed || "N/A";

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
      <h3 className="text-lg font-bold mb-4">{t("emotional.emotionalAnalysis")}</h3>
      <div className="space-y-4">
        <div>
          <p className="text-sm text-slate-600 mb-3">{summaryInsight}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 mb-1">{t("emotional.overallEmotionalState")}</h4>
            <span className="text-lg font-bold text-green-600 capitalize">{overallEmotionalState}</span>
          </div>

          <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
            <h4 className="font-semibold text-orange-800 mb-1">{t("emotional.frustration")}</h4>
            <span className="text-lg font-bold text-orange-600 capitalize">{frustrationLevel}</span>
          </div>

          <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-800 mb-1"> {t("emotional.confidence")}</h4>
            <span className="text-lg font-bold text-red-600 capitalize">{confidenceTrend}</span>
          </div>

          <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-1">{t("emotional.motivation")}</h4>
            <span className="text-lg font-bold text-blue-600 capitalize">{motivationLevel}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-semibold mb-2 text-red-700"> {t("emotional.stressIndicators")}</h4>
            {stressIndicators.length > 0 ? (
              <ul className="space-y-1">
                {stressIndicators.map((indicator, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-slate-700 capitalize">{indicator}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500"> {t("emotional.noStressIndicators")}</p>
            )}
          </div>

          <div>
            <h4 className="font-semibold mb-2 text-green-700"> {t("emotional.supportRecommendations")}</h4>
            {supportRecommendations.length > 0 ? (
              <ul className="space-y-1">
                {supportRecommendations.map((rec, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Lightbulb className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{rec}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">{t("emotional.noRecommendations")}</p>
            )}
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <h4 className="font-semibold text-yellow-800">{t("emotional.interventionNeeded")}</h4>
          </div>
          <p className="text-sm text-yellow-700 capitalize">
            {interventionNeeded} {t("emotional.interventionLevelText")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmotionalSection;
